import { useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, Brain, FileText, Files, ListChecks, Receipt, Stack, Warning } from '@phosphor-icons/react';
import { useCompany, useWorkspace } from '../App';
import { api } from '../lib/api';
import { compactMoney, faNumber, formatMoney, jalaliDate, metricDisplay, severityLabel, statementLabel, statusLabel } from '../lib/format';
import { Card, EmptyState, InlineNotice, LoadingState, PageHeader, ProgressRing, StatusBadge } from '../components/Ui';
import { ScoreBands, ScoreRing, TrendChart } from '../components/Charts';
import type { Dashboard, InsightItem } from '../types';

/**
 * §۲ شاخص‌های سریع — فروش، سود، هزینه، جریان نقد.
 *
 * These four and in this order because the spec names them; the ratios that
 * used to sit here are still on the page, one card down, where they belong
 * among the other شاخص‌ها rather than displacing the figures a finance lead
 * opens the page for.
 */
const HEADLINE_METRICS = ['revenue', 'net_profit', 'total_expenses', 'operating_cash_flow'];

/** §۶ تحلیل زنده مالی keeps هشدارها, فرصت‌ها and پیشنهادها apart. So does this. */
const INSIGHT_GROUPS: Array<{ kind: string; titleFa: string }> = [
  { kind: 'alert', titleFa: 'هشدارها' },
  { kind: 'opportunity', titleFa: 'فرصت‌ها' },
  { kind: 'suggestion', titleFa: 'پیشنهادها' },
  { kind: 'observation', titleFa: 'مشاهده‌ها' },
];

/** Periods the trend chart can be drawn over, coarsest first. */
const TREND_SCALES: Array<{ key: string; labelFa: string; match: (periodKey: string) => boolean }> = [
  { key: 'month', labelFa: 'ماهانه', match: (key) => key.startsWith('m') },
  { key: 'quarter', labelFa: 'سه‌ماهه', match: (key) => key.startsWith('q') },
  { key: 'half', labelFa: 'شش‌ماهه', match: (key) => key.startsWith('h') },
];

export function DashboardPage() {
  const { fiscalYear, periodKey, currency, navigate } = useWorkspace();
  const company = useCompany();
  const [data, setData] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scale, setScale] = useState('month');
  const [trendMetric, setTrendMetric] = useState('revenue');
  // Starts as the wording the server minted, and is replaced by the analyst's
  // sentence if one arrives. Never the other way round.
  const [criticalNote, setCriticalNote] = useState('');

  useEffect(() => {
    if (!fiscalYear) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    setError('');
    void api.dashboard(company.id, fiscalYear.id, periodKey)
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setCriticalNote(result.criticalNoteFa ?? '');
        if (!result.criticalNoteFa) return;
        // Best effort, and deliberately not awaited: the page is already
        // correct without it. A failure here leaves the server's wording.
        void api.qualityExplanation(company.id, fiscalYear.id, periodKey)
          .then((explained) => { if (!cancelled && explained.noteFa) setCriticalNote(explained.noteFa); })
          .catch(() => undefined);
      })
      .catch((caught: Error) => { if (!cancelled) setError(caught.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [company.id, fiscalYear, periodKey]);

  const trend = data?.trend;
  const trendPeriods = useMemo(() => {
    const matcher = TREND_SCALES.find((item) => item.key === scale) ?? TREND_SCALES[0];
    return (trend?.periods ?? []).filter((period) => matcher.match(period.key));
  }, [trend, scale]);

  // A scale with no computed periods is not offered. Showing an empty frame
  // behind a button the reader just pressed reads as a broken chart.
  const availableScales = useMemo(
    () => TREND_SCALES.filter((item) => (trend?.periods ?? []).some((period) => item.match(period.key))),
    [trend],
  );

  useEffect(() => {
    if (availableScales.length && !availableScales.some((item) => item.key === scale)) {
      setScale(availableScales[0].key);
    }
  }, [availableScales, scale]);

  if (loading) return <LoadingState label="در حال ساخت مرکز فرمان مالی" />;
  if (error) return <InlineNotice tone="danger">{error}</InlineNotice>;

  if (!fiscalYear) {
    return (
      <EmptyState
        title="سال مالی تعریف نشده است"
        description="برای این شرکت هنوز سال مالی ثبت نشده. ابتدا سال مالی را در صفحه شرکت‌ها بسازید."
        action={<button className="button button--primary" onClick={() => navigate('companies')}>رفتن به شرکت‌ها</button>}
      />
    );
  }

  if (!data?.ready) {
    return (
      <EmptyState
        icon={<Files size={28} />}
        title="هنوز داده‌ای پردازش نشده است"
        description={data?.reasonFa ?? 'برای دیدن ارقام، دفتر کل یا تراز آزمایشی این سال مالی را بارگذاری کنید.'}
        action={<button className="button button--primary" onClick={() => navigate('processing')}>بارگذاری داده</button>}
      />
    );
  }

  const metrics = data.metrics ?? [];
  const headline = HEADLINE_METRICS
    .map((key) => metrics.find((metric) => metric.key === key))
    .filter((metric): metric is NonNullable<typeof metric> => Boolean(metric));
  const rest = metrics.filter((metric) => !HEADLINE_METRICS.includes(metric.key));
  const insights = data.insights ?? [];
  const issues = data.issues ?? [];
  const counts = data.counts;
  const health = data.health;
  const quality = data.quality;
  const activity = data.activity;

  const trendSeries = (trend?.series ?? []).filter((series) => series.key === trendMetric);
  const trendUnit = trend?.series.find((series) => series.key === trendMetric)?.unit ?? 'amount';
  const formatTrend = (value: number) => (trendUnit === 'amount'
    ? compactMoney(value, company.reportingCurrency, currency)
    : trendUnit === 'percent' ? `${faNumber(value, 1)}٪` : faNumber(value, 2));

  const groupedInsights = INSIGHT_GROUPS
    .map((group) => ({ ...group, items: insights.filter((insight) => insight.kind === group.kind) }))
    .filter((group) => group.items.length > 0);
  const untyped = insights.filter((insight) => !INSIGHT_GROUPS.some((group) => group.kind === insight.kind));
  if (untyped.length) groupedInsights.push({ kind: 'other', titleFa: 'سایر یافته‌ها', items: untyped });

  return (
    <>
      <PageHeader
        eyebrow="مرکز فرمان مالی"
        title={company.name}
        description={`${fiscalYear.labelFa} — ${periodKey === 'full' ? 'کل سال مالی' : 'دوره انتخاب‌شده'}`}
        actions={(
          <button className="button button--soft" onClick={() => navigate('statements')}>
            صورت‌های مالی <ArrowUpRight size={16} />
          </button>
        )}
      />

      {/* §۲ اطلاعات بالای صفحه — آخرین بروزرسانی و وضعیت تحلیل AI. */}
      {activity && (
        <div className="status-strip">
          <span>
            آخرین بروزرسانی:
            <b>{activity.lastRun ? jalaliDate(activity.lastRun.at, 'long') : 'بدون پردازش'}</b>
          </span>
          <span>
            وضعیت تحلیل هوش مالی:
            <b>
              {!activity.ai.configured
                ? 'غیرفعال'
                : activity.ai.lastAnalysisAt
                  ? `آخرین تحلیل ${jalaliDate(activity.ai.lastAnalysisAt)}`
                  : 'آماده، هنوز تحلیلی انجام نشده'}
            </b>
          </span>
        </div>
      )}

      {!data.reliable && (
        <InlineNotice tone="danger">
          {/*
            The engine decides that the period is unreliable; what that means
            for the reader is written from the findings themselves. This used to
            be one hard-coded sentence — «صورت‌های مالی این دوره تراز نیست» —
            which stopped being true the moment a second critical finding
            existed, because figures counted twice balance perfectly.
          */}
          {criticalNote || 'این دوره یافتهٔ بحرانی دارد؛ تا رفع آن، ارقام زیر را مبنای تصمیم قرار ندهید.'}
          {' '}فهرست کامل یافته‌ها در پایین همین صفحه است.
        </InlineNotice>
      )}

      <div className="metric-grid">
        {headline.map((metric) => (
          <Card key={metric.key} className={metric.value === null ? 'metric-card metric-card--empty' : 'metric-card'}>
            <div className="metric-card__head"><span>{metric.labelFa}</span></div>
            <strong>{metricDisplay(metric, company.reportingCurrency, currency)}</strong>
          </Card>
        ))}
      </div>

      <div className="dashboard-grid">
        {/* §۲ خلاصه وضعیت مالی — سلامت مالی. */}
        {health && (
          <Card className="score-card">
            <div className="card-heading"><h2>سلامت مالی</h2></div>
            <ScoreRing
              score={health.score}
              coverage={health.coverage}
              labelFa="امتیاز سلامت مالی"
              captionFa={health.score === null
                ? 'هیچ‌کدام از شاخص‌های مبنا برای این دوره قابل محاسبه نبود.'
                // The score is only ever as good as the figures under it, and
                // this card is read at a glance — it has to carry the caveat
                // itself rather than rely on a banner further up the page.
                : data.reliable === false
                  ? `بر پایه ارقامی محاسبه شده که یافتهٔ بحرانی رفع‌نشده دارند؛ تا رفع آن، این امتیاز معتبر نیست.`
                  : `بر پایه ${faNumber(Math.round(health.coverage * 100))}٪ از شاخص‌های مبنا محاسبه شده است.`}
            />
            <ScoreBands contributions={health.contributions} />
          </Card>
        )}

        {trend && availableScales.length > 0 && (
          <Card className="trend-card">
            <div className="card-heading">
              <h2>روند شاخص‌های کلیدی</h2>
              <div className="segmented" role="group" aria-label="بازه روند">
                {availableScales.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={scale === item.key ? 'active' : ''}
                    aria-pressed={scale === item.key}
                    onClick={() => setScale(item.key)}
                  >
                    {item.labelFa}
                  </button>
                ))}
              </div>
            </div>
            <label className="field field--inline">
              <span className="smart-field__label">شاخص</span>
              <select value={trendMetric} onChange={(event) => setTrendMetric(event.target.value)}>
                {(trend.series ?? []).map((series) => (
                  <option key={series.key} value={series.key}>{series.labelFa}</option>
                ))}
              </select>
            </label>
            <TrendChart
              periods={trendPeriods}
              series={trendSeries}
              format={formatTrend}
              caption={`روند ${trendSeries[0]?.labelFa ?? ''}`}
            />
            <p className="hint">دوره‌ها از راست به چپ، از ابتدای سال مالی. دوره‌ای که محاسبه نشده رسم نمی‌شود.</p>
          </Card>
        )}

        <Card className="metrics-card">
          <div className="card-heading">
            <h2>شاخص‌های دوره</h2>
            <span className="data-badge">{faNumber(metrics.length)} شاخص</span>
          </div>
          <div className="health-list">
            {rest.map((metric) => (
              <div key={metric.key} className="health-list__row">
                <span>{metric.labelFa}</span>
                <b className={metric.value === null ? 'is-missing' : ''}>
                  {metricDisplay(metric, company.reportingCurrency, currency)}
                </b>
              </div>
            ))}
            {rest.length === 0 && <p className="hint">شاخص دیگری برای این دوره محاسبه نشد.</p>}
          </div>
        </Card>

        <Card className="alerts-card">
          <div className="card-heading"><h2>یافته‌های تحلیل</h2></div>
          {groupedInsights.length === 0 ? (
            <p className="hint">در این دوره یافته‌ای ثبت نشده است.</p>
          ) : (
            groupedInsights.map((group) => (
              <div key={group.kind} className="insight-group">
                <h3>{group.titleFa}</h3>
                <ul className="alert-list">
                  {group.items.map((insight: InsightItem, index: number) => (
                    <li key={`${insight.titleFa}-${index}`}>
                      <span className={`severity severity--${insight.severity}`}>{severityLabel(insight.severity)}</span>
                      <div>
                        <strong>{insight.titleFa}</strong>
                        <p>{insight.bodyFa}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </Card>

        <Card className="health-card">
          <div className="card-heading"><h2>وضعیت داده</h2></div>
          {quality && (
            <ScoreRing
              score={quality.score}
              labelFa="کیفیت اطلاعات"
              // Smaller than the health ring: this is a statement about the
              // input, not about the company.
              size={96}
              captionFa={quality.completeness === null
                ? 'هنوز حسابی برای سنجش طبقه‌بندی وجود ندارد.'
                : `${faNumber(Math.round(quality.completeness * 100))}٪ از حساب‌ها طبقه‌بندی شده‌اند.`}
            />
          )}
          {data.latestRun && (
            <div className="run-facts">
              {/* The progress ring is for a run that is still going. On a
                  finished one it always reads ۱۰۰, which next to the quality
                  score put two rings showing the same number in one card and
                  invited the reader to compare them. The badge already says the
                  run finished. */}
              {data.latestRun.status !== 'succeeded' && (
                <ProgressRing value={data.latestRun.progress} label="پیشرفت" />
              )}
              <div>
                <StatusBadge status={data.latestRun.status} label={statusLabel(data.latestRun.status)} />
                <p>{data.latestRun.messageFa}</p>
              </div>
            </div>
          )}
          {counts && (
            <div className="health-list">
              <div className="health-list__row"><span><Files size={16} /> فایل‌های بارگذاری‌شده</span><b>{faNumber(counts.files)}</b></div>
              <div className="health-list__row"><span><ListChecks size={16} /> جدول‌های شناسایی‌شده</span><b>{faNumber(counts.datasets)}</b></div>
              <div className="health-list__row"><span><Receipt size={16} /> ردیف‌های دفتر</span><b>{faNumber(counts.entries)}</b></div>
            </div>
          )}
          {issues.length > 0 && (
            <ul className="alert-list">
              {issues.slice(0, 4).map((issue) => (
                <li key={issue.titleFa}>
                  <span className={`severity severity--${issue.severity}`}>
                    <Warning size={13} /> {severityLabel(issue.severity)}
                  </span>
                  <div>
                    <strong>{issue.titleFa}</strong>
                    <p>{faNumber(issue.count)} مورد</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <button className="button button--ghost button--small" onClick={() => navigate('processing')}>
            مشاهده کامل کیفیت داده
          </button>
        </Card>

        {/* §۲ فعالیت‌های اخیر — آخرین پردازش، آخرین گزارش، آخرین تحلیل AI.
            Placed after «وضعیت داده» rather than before it: this card is full
            width, so with it in the middle the three-card row above could not
            close and left a quarter of the grid empty. */}
        {activity && (
          <Card className="activity-card">
            <div className="card-heading"><h2>فعالیت‌های اخیر</h2></div>
            <ul className="recent-list">
              <li>
                <Stack size={17} />
                <div>
                  <strong>آخرین پردازش</strong>
                  <p>{activity.lastRun
                    ? `${statusLabel(activity.lastRun.status)} — ${jalaliDate(activity.lastRun.at, 'long')}`
                    : 'هنوز پردازشی انجام نشده است.'}</p>
                </div>
                <button className="button button--ghost button--small" onClick={() => navigate('processing')}>پردازش</button>
              </li>
              <li>
                <FileText size={17} />
                <div>
                  <strong>آخرین گزارش</strong>
                  <p>{activity.lastReport
                    ? `${activity.lastReport.titleFa} — ${jalaliDate(activity.lastReport.at, 'long')}`
                    : 'هنوز گزارشی ساخته نشده است.'}</p>
                </div>
                <button className="button button--ghost button--small" onClick={() => navigate('reports')}>گزارش‌ها</button>
              </li>
              <li>
                <Brain size={17} />
                <div>
                  <strong>آخرین تحلیل هوش مالی</strong>
                  <p>{activity.lastAnalysis
                    ? `${activity.lastAnalysis.titleFa || 'گفتگوی بدون عنوان'} — ${jalaliDate(activity.lastAnalysis.at, 'long')}`
                    : 'هنوز تحلیلی انجام نشده است.'}</p>
                </div>
                <button className="button button--ghost button--small" onClick={() => navigate('ai')}>مرکز هوش</button>
              </li>
            </ul>
          </Card>
        )}

        {data.totals && (
          <Card className="span-full">
            <div className="card-heading"><h2>جمع‌های صورت‌های مالی</h2></div>
            {/* Grouped by statement, not flattened. The balance sheet and the
                equity statement both publish a «حقوق مالکانه» total and they
                differ by the period result; side by side in one undifferentiated
                grid they read as two answers to the same question. */}
            {Object.entries(data.totals).map(([kind, values]) => (
              <div key={kind} className="totals-group">
                <h3>{statementLabel(kind)}</h3>
                <div className="totals-grid">
                  {Object.entries(values).map(([label, value]) => (
                    <div key={`${kind}-${label}`} className="totals-grid__item">
                      <span>{label}</span>
                      <b>{formatMoney(value, company.reportingCurrency, currency)}</b>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </Card>
        )}
      </div>
    </>
  );
}
